import { getChatInstance, SPR_CHAT_EVENTS, getSprinklrContext } from './sprinklr-chat-service';
import { logger } from '../../shared/utils/logger';
import { MessageType } from '../../shared/message';

/**
 * @typedef {Object} SprinklrChatMessage
 * @property {string}  id
 * @property {string}  text
 * @property {string}  sender   – agent senders start with 'P_' or 'p_'
 * @property {number}  timestamp
 * @property {Object}  [metadata]
 */

/**
 * Utility to parse message text that may contain HTML from Sprinklr into plain text.
 * This is a simple implementation and may need to be enhanced to handle all edge cases.
 *
 * @param {string} text - The raw message text from Sprinklr, which may contain HTML.
 * @returns {string} - The parsed plain text message.
 */
const parseAgentMessageText = (text) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'text/html');
  return doc.body.textContent || '';
};

/**
 * Creates a new Sprinklr conversation.
 *
 * `args` is spread into the SDK's createConversation params, so anything added to
 * it is sent to Sprinklr. The session and deviceType are deliberately separate
 * parameters rather than members of `args`: they are only needed to build the
 * conversation context, and spreading the session would put the whole object —
 * session ID, conversation ID, case number, escalation state — into the SDK call.
 *
 * @param {Object} args - Params forwarded to the SDK, e.g. { initialMessages }.
 * @param {Object} [session] - The assistant's session (the `maa-session` record).
 * @param {'app'|'mobile'|'desktop'} [deviceType] - The surface the escalation came from.
 * @returns {Promise<string>} The conversation ID.
 */
export async function startConversation(args, session, deviceType) {
  const chat = getChatInstance();

  if (typeof chat.createConversation !== 'function') {
    throw new Error('createConversation() not available on the SDK.');
  }

  const conversationContext = await getSprinklrContext(session, deviceType).catch((err) => {
    // eslint-disable-next-line no-console
    console.error('[Sprinklr] Failed to get context for conversation:', err);
    return {};
  });

  const params = {
    pageTitle: typeof document !== 'undefined' ? document.title : 'Magic Apron',
    pageUrl: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    timeZone: typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : 'America/New_York',
    locale: 'en',
    createCase: true,
    conversationContext,
    ...args
  };

  logger.log('[Sprinklr] Creating conversation with params…', params);
  let result;
  try {
    result = await chat.createConversation(params);
  } catch (err) {
    logger.error('[Sprinklr] createConversation() failed:', err);
    throw err;
  }

  logger.log('[Sprinklr] createConversation raw response:', result);

  const conversationId = result?.id
    || result?.conversationId
    || result?.conversation?.id;
  const caseNumber = result?.conversation?.caseNumber ?? null;

  if (!conversationId) {
    throw new Error('Could not extract conversation id from createConversation response.');
  }

  logger.log('[Sprinklr] Conversation created:', conversationId, 'caseNumber:', caseNumber);
  return { conversationId, caseNumber };
}

/**
 * Sends a text message in the current Sprinklr conversation.
 * Payload matches the working POC: { id, conversationId, message, isSentByUser }.
 *
 * @param {string} text
 */
export async function sendMessage(text, conversationId, actionId) {
  const chat = getChatInstance();

  if (!conversationId) {
    throw new Error('No active conversation. Call startConversation() first.');
  }

  if (typeof chat.sendMessage !== 'function') {
    throw new Error('sendMessage() not available on the SDK.');
  }

  const payload = {
    id: actionId,
    conversationId,
    message: { text },
    isSentByUser: true,
  };

  logger.log('[Sprinklr] Sending payload to live agent:', JSON.stringify(payload, null, 2));

  const response = await chat.sendMessage(payload).catch((err) => {
    // eslint-disable-next-line no-console
    console.error('[Sprinklr] Failed to send message via SDK:', err);
    throw err;
  });

  logger.log('[Sprinklr] Message sent successfully');
}

/**
 * Sends a message with an image attachment in the current Sprinklr conversation.
 *
 * @param {string} text - Optional text to accompany the image.
 * @param {File} file - The image File object to attach.
 * @param {string} [conversationId] - Explicit conversation ID (e.g. from persisted session).
 * @param {string} [actionId] - Unique per-message ID for SDK correlation.
 */
export async function sendMessageWithImage(text, file, conversationId, actionId) {
  const chat = getChatInstance();

  if (!conversationId) {
    throw new Error('No active conversation. Call startConversation() first.');
  }

  if (typeof chat.sendMessage !== 'function') {
    throw new Error('sendMessage() not available on the SDK.');
  }

  if (!file) {
    throw new Error('A file is required to send an image message.');
  }

  const payload = {
    id: actionId || `img-${crypto.randomUUID()}`,
    conversationId,
    message: {
      text: text || '',
      mediaAttachments: [
        {
          file,
          id: `MEDIA_${crypto.randomUUID()}`,
          title: file.name,
          type: 'IMAGE',
        },
      ],
    },
    isSentByUser: true,
  };

  logger.log('[Sprinklr] Sending image message to live agent:', payload.message.mediaAttachments[0].title);

  await chat.sendMessage(payload).catch((err) => {
    // eslint-disable-next-line no-console
    console.error('[Sprinklr] Failed to send image message via SDK:', err);
    throw err;
  });

  logger.log('[Sprinklr] Image message sent successfully');
}

/**
 * Parse a MESSAGE_RECEIVED event and return the survey URL if present.
 * @param {object} event - The raw MESSAGE_RECEIVED event payload.
 * @returns {string|null} The survey URL, or null if not a survey message.
 */
function parseSurveyUrl(event) {
  const payload = event?.payload || event;
  const msg = payload?.chatMessage || payload;
  const attachment = msg?.messagePayload?.attachment;
  if (attachment?.type === 'SURVEY') {
    return attachment.surveyButton?.surveyUrl || null;
  }
  return null;
}

/**
 * Subscribes to incoming agent messages using the SDK's `subscribeToEvents`.
 * Only forwards messages from agents (sender starts with 'P_' or 'p_').
 *
 * @param {(messages: SprinklrChatMessage[]) => void} onNewMessage
 * @param {() => void} [onConversationEnd]
 * @param {(surveyUrl: string) => void} onSurveyReceived
 * @returns {() => void} Unsubscribe function.
 */
export function onMessageReceived({ onNewMessage, onConversationEnd, onSurveyReceived, onImageReceived }) {
  const chat = getChatInstance();

  if (typeof chat.subscribeToEvents !== 'function') {
    logger.warn('[Sprinklr] subscribeToEvents() not available on SDK.');
    return () => {};
  }

  let unsubscribeFn = null;

  try {
    const result = chat.subscribeToEvents({
      topics: [
        SPR_CHAT_EVENTS.MESSAGE_RECEIVED,
        SPR_CHAT_EVENTS.CONVERSATIONS_UPDATED,
      ],
      subscriber: (event) => {
        const type = event?.type || '';

        logger.log('[Sprinklr] Event received:', type, event);

        if (type === SPR_CHAT_EVENTS.MESSAGE_RECEIVED) {
          const data = event?.payload || event;
          const msg = data.chatMessage || data;
          const msgPayload = msg.messagePayload || {};
          const text = msgPayload.text || msg.text || '';
          const sender = msg.sender || '';
          const isAgent = sender.startsWith('P_') || sender.startsWith('p_');

          // Survey messages carry a URL attachment, not agent text.
          // Extract the URL and exit early — there's nothing to forward as a chat message.
          const surveyUrl = parseSurveyUrl(event);
          if (surveyUrl) {
            onSurveyReceived(surveyUrl);
            return;
          }

          // Check for image attachments (e.g. user echo after upload, or an agent image)
          const mediaAttachments = msgPayload.mediaAttachments || [];
          const imageAttachment = mediaAttachments.find((attachment) => attachment.type === 'IMAGE' && attachment.url);
          if (imageAttachment && onImageReceived) {
            onImageReceived({
              url: imageAttachment.url,
              title: imageAttachment.title || '',
              messageId: msg.id,
              isAgent,
              // For an agent image, hand over any accompanying text + timestamp so the
              // handler saves ONE message (text + image together) instead of this block
              // also emitting a separate text-only message below.
              text: isAgent ? parseAgentMessageText(text) : undefined,
              timestamp: msg.creationTime || Date.now(),
            });
            // The image handler owns this message: a user echo patches the user message,
            // an agent image saves a combined agent message. Don't also emit a text message.
            if (isAgent) return;
          }

          // Only forward agent messages (agent senders start with P_ or p_)
          if (!isAgent || !text) return;

          const messageText = parseAgentMessageText(text);
          const message = {
            type: MessageType.AGENT,
            content: messageText,
            messageId: msg.id,
            timestamp: msg.creationTime || Date.now(),
          };

          onNewMessage(message);
        }

        if (type === SPR_CHAT_EVENTS.CONVERSATIONS_UPDATED) {
          const data = event?.payload;
          const sender = data.sender || '';
          const isAgent = sender.startsWith('P_') || sender.startsWith('p_');
          if (data.closed) {
            logger.log(`[Sprinklr] Conversation closed event${isAgent ? ' by agent' : ''}`, {
              conversationId: data.conversationId,
            });
            if (onConversationEnd) {
              onConversationEnd(isAgent);
            }
          }
        }
      },
    });

    // The SDK may return the unsubscribe fn directly (sync) or as a Promise
    if (result && typeof result.then === 'function') {
      result.then((unsub) => { unsubscribeFn = unsub; }).catch(() => {});
    } else if (typeof result === 'function') {
      unsubscribeFn = result;
    }
  } catch (err) {
    logger.error('[Sprinklr] Failed to subscribe to events:', err);
  }

  // Return unsubscribe function
  return () => {
    if (typeof unsubscribeFn === 'function') {
      unsubscribeFn();
    }
  };
}
/**
 * Ends the current conversation and resets local state.
 */
export async function endConversation(convId) {
  logger.log('[Sprinklr - endConversation] :', convId);
  if (convId) {
    const chat = getChatInstance();
    if (chat && typeof chat.closeConversation === 'function') {
      try {
        await chat.closeConversation({ conversationId: convId });
      } catch (err) {
        logger.error('[Sprinklr] Failed to end conversation via SDK:', err);
      }
    }
  }
}

export async function getMessages({ conversationId, limit = 5 }) {
  const chat = getChatInstance();

  if (typeof chat.getMessages !== 'function') {
    throw new Error('getMessages() not available on the SDK.');
  }
  if (!conversationId) {
    throw new Error('conversationId is required to fetch messages.');
  }

  // Not `getConversations`: its `chatMessages` holds only what the SDK already has locally
  // (empty on a fresh page load), and its list can drop an active conversation entirely.
  // Errors are swallowed because the caller reads a rejection as "the conversation ended".
  let result = null;
  try {
    result = await chat.getMessages({
      conversationId,
      size: limit,
      includeConversation: true,
    });
  } catch (err) {
    // Matched on `message`, per the ALREADY_INITIATED precedent in sprinklr-chat-service.
    if (err?.message === 'NOT_FOUND') {
      logger.warn('[Sprinklr] Conversation no longer exists:', conversationId);
      return { messages: [], closed: true };
    }
    logger.error('[Sprinklr] getMessages() failed:', err);
    return { messages: [], closed: false };
  }

  const recentMessages = result?.messages || [];
  let messagesToAdd = [];
  recentMessages.forEach((msg) => {
    const isAgent = msg.sender.startsWith('P_') || msg.sender.startsWith('p_');
    if (!isAgent) return;

    const messageText = parseAgentMessageText(msg.messagePayload?.text || msg.text || '');
    const imageAttachment = (msg.messagePayload?.mediaAttachments || [])
      .find((attachment) => attachment.type === 'IMAGE' && attachment.url);
    if (!messageText && !imageAttachment) return;

    messagesToAdd.push({
      messageId: msg.id || `spr-${Date.now()}`,
      content: messageText,
      type: MessageType.AGENT,
      timestamp: msg.creationTime || Date.now(),
      ...(imageAttachment && {
        isImageAttached: true,
        storageLink: imageAttachment.url,
        imageFileName: imageAttachment.title || '',
      }),
    });
  });

  // An unknown state must not read as closed: the caller ends the chat on a truthy value.
  return { messages: messagesToAdd, closed: result?.conversation?.closed ?? false };
}